import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { localStorageFields } from '@/constants/localStorage';
import { MAX_FONT_SCALE, MIN_FONT_SCALE, scaleMap, SettingName, Theme, Themes } from './constants'
import { notifyThemeChange } from '@/utils/app'
import type { IBiometryOptions } from '@/interfaces/general'

const FIELD_NAME = localStorageFields.deviceSettings

interface ISettings {
  activeTab: SettingName | null
  fontScale: number
  theme: Theme
  biometry: IBiometryOptions
}

const changeCssFontScale = (value: number) => {
  const mapValue = scaleMap[value] || 1
  document.documentElement.style.setProperty('--font-scale', '' + mapValue)
}

const changeTheme = (theme: Theme) => {
  let localTheme = theme
  if (localTheme === Themes.system) {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    if (isDark) {
      localTheme = Themes.dark
    } else {
      localTheme = Themes.light
    }
  }
  document.documentElement.dataset.theme = localTheme
  notifyThemeChange(theme)
}

const defaultSettings = JSON.parse('' + localStorage.getItem(FIELD_NAME)) as Partial<ISettings> | null

const initialState: ISettings = {
  fontScale: 1,
  theme: 'light',
  biometry: { enabled: false },
  ...defaultSettings,
  activeTab: null,
}

changeCssFontScale(initialState.fontScale)
changeTheme(initialState.theme)

const settings = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setActiveTab: (state, { payload }: PayloadAction<SettingName | null>) => {
      state.activeTab = payload
    },
    resetTabs: (state, _: PayloadAction<undefined | null>) => {
      state.activeTab = null
    },
    setFontScale(state, {payload}: { payload: number }) {
      if (payload > MAX_FONT_SCALE || payload < MIN_FONT_SCALE) {
        return
      }
      state.fontScale = payload
      localStorage.setItem(FIELD_NAME, JSON.stringify(state))
      changeCssFontScale(payload)
    },
    setTheme(state, {payload}: PayloadAction<Theme>) {
      state.theme = payload
      localStorage.setItem(FIELD_NAME, JSON.stringify(state))
      changeTheme(payload)
    },
    setBiometry(state, {payload}: PayloadAction<IBiometryOptions>) {
      state.biometry = payload
      localStorage.setItem(FIELD_NAME, JSON.stringify(state))
    }
  },
})


export const {
  setActiveTab,
  resetTabs,
  setFontScale,
  setTheme,
  setBiometry
} = settings.actions
export default settings.reducer
